/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.dashboard

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.outlined.Notifications
import androidx.compose.material3.Badge
import androidx.compose.material3.BadgedBox
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import studio.oddpods.cauce.core.model.dashboard.PatientStatus
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.core.ui.scaffold.rememberKptPullToRefreshState
import studio.oddpods.cauce.feature.dashboard.generated.resources.Res
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_collected
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_last_session
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_no_data
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_outstanding
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_overdue
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_paid
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_patients
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_pending
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_reminders_count
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_reminders_title
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_status_overdue
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_status_pending
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_status_up_to_date
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_title

@Composable
fun DashboardScreen(
    onPatientClick: (String) -> Unit,
    modifier: Modifier = Modifier,
    onOpenReminders: () -> Unit = {},
    viewModel: DashboardViewModel = koinViewModel(),
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()

    KptScaffold(
        showNavigationIcon = false,
        title = stringResource(Res.string.dashboard_title),
        modifier = modifier,
        pullToRefreshState = rememberKptPullToRefreshState(
            isEnabled = !state.isLoading,
            isRefreshing = state.isRefreshing,
            onRefresh = { viewModel.trySendAction(DashboardAction.Refresh) },
        ),
    ) {
        when {
            state.isLoading -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator()
                }
            }

            state.error != null -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(state.error!!.asString(), color = MaterialTheme.colorScheme.error)
                }
            }

            state.summary != null -> {
                val summary = state.summary!!
                LazyColumn(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(horizontal = 16.dp),
                ) {
                    if (state.pendingReminderCount >= 1) {
                        item {
                            PendingRemindersAlert(
                                count = state.pendingReminderCount,
                                onClick = onOpenReminders,
                            )
                        }
                    }

                    item {
                        PendingConfirmationsSection(
                            sessions = state.pendingConfirmations,
                            savingSessionId = state.savingSessionId,
                            onConfirm = { viewModel.trySendAction(DashboardAction.ConfirmSession(it)) },
                            onCancel = { viewModel.trySendAction(DashboardAction.CancelSession(it)) },
                            modifier = Modifier.padding(vertical = 8.dp),
                        )
                    }

                    item {
                        DashboardBanner(
                            showBanner = state.showBanner,
                            bannerText = state.bannerText,
                            onDismiss = { viewModel.trySendAction(DashboardAction.BannerDismissed) },
                        )
                    }

                    item {
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = state.currentMonth,
                            style = MaterialTheme.typography.titleMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                        Spacer(modifier = Modifier.height(12.dp))
                    }

                    item {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(12.dp),
                        ) {
                            SummaryCard(
                                label = stringResource(Res.string.dashboard_collected),
                                value = state.totalCollectedDisplay,
                                modifier = Modifier.weight(1f),
                            )
                            SummaryCard(
                                label = stringResource(Res.string.dashboard_outstanding),
                                value = state.totalOutstandingDisplay,
                                modifier = Modifier.weight(1f),
                            )
                        }
                        Spacer(modifier = Modifier.height(12.dp))
                    }

                    item {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(12.dp),
                        ) {
                            SummaryCard(
                                label = stringResource(Res.string.dashboard_paid),
                                value = summary.paidCount.toString(),
                                modifier = Modifier.weight(1f),
                            )
                            SummaryCard(
                                label = stringResource(Res.string.dashboard_pending),
                                value = summary.pendingCount.toString(),
                                modifier = Modifier.weight(1f),
                            )
                            SummaryCard(
                                label = stringResource(Res.string.dashboard_overdue),
                                value = summary.overdueCount.toString(),
                                modifier = Modifier.weight(1f),
                            )
                        }
                        Spacer(modifier = Modifier.height(20.dp))
                    }

                    item {
                        Text(
                            text = stringResource(Res.string.dashboard_patients),
                            style = MaterialTheme.typography.titleMedium,
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                    }

                    if (state.patientsDisplay.isEmpty()) {
                        item {
                            Text(
                                text = stringResource(Res.string.dashboard_no_data),
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                    } else {
                        items(state.patientsDisplay) { patient ->
                            PatientSummaryCard(
                                patient,
                                onClick = { onPatientClick(patient.patientId) },
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                        }
                    }

                    item { Spacer(modifier = Modifier.height(16.dp)) }
                }
            }

            else -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator()
                }
            }
        }
    }
}

@Composable
private fun DashboardBanner(showBanner: Boolean, bannerText: UiText?, onDismiss: () -> Unit) {
    AnimatedVisibility(showBanner && bannerText != null) {
        Surface(
            color = MaterialTheme.colorScheme.primaryContainer,
            modifier = Modifier
                .fillMaxWidth()
                .padding(8.dp)
                .clickable { onDismiss() },
            shape = RoundedCornerShape(8.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(12.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = bannerText?.asString() ?: "",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.bodyMedium,
                )
                Icon(Icons.Default.Close, contentDescription = null)
            }
        }
    }
}

@Composable
private fun PendingRemindersAlert(count: Int, onClick: () -> Unit) {
    Card(
        onClick = onClick,
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.errorContainer,
        ),
    ) {
        Row(
            modifier = Modifier.padding(16.dp).fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.dashboard_reminders_title),
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.onErrorContainer,
                )
                Text(
                    text = stringResource(Res.string.dashboard_reminders_count, count),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onErrorContainer,
                )
            }
            BadgedBox(badge = { Badge() }) {
                Icon(
                    imageVector = Icons.Outlined.Notifications,
                    contentDescription = null,
                    modifier = Modifier.size(24.dp),
                    tint = MaterialTheme.colorScheme.onErrorContainer,
                )
            }
        }
    }
}

@Composable
private fun SummaryCard(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
) {
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant,
        ),
    ) {
        Column(
            modifier = Modifier.padding(12.dp),
        ) {
            Text(
                text = label,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                text = value,
                style = MaterialTheme.typography.titleMedium,
            )
        }
    }
}

@Composable
private fun PatientSummaryCard(
    patient: PatientPaymentSummaryDisplay,
    onClick: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
    ) {
        Row(
            modifier = Modifier.padding(12.dp).fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column {
                Text(
                    text = patient.patientName,
                    style = MaterialTheme.typography.bodyLarge,
                )
                if (patient.lastSessionDateDisplay != null) {
                    Text(
                        text = stringResource(
                            Res.string.dashboard_last_session,
                            patient.lastSessionDateDisplay,
                        ),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(
                    text = patient.totalOwedDisplay,
                    style = MaterialTheme.typography.bodyMedium,
                )
                Text(
                    text = when (patient.status) {
                        PatientStatus.UP_TO_DATE -> stringResource(Res.string.dashboard_status_up_to_date)
                        PatientStatus.PENDING -> stringResource(Res.string.dashboard_status_pending)
                        PatientStatus.OVERDUE -> stringResource(Res.string.dashboard_status_overdue)
                    },
                    style = MaterialTheme.typography.labelSmall,
                    color = when (patient.status) {
                        PatientStatus.UP_TO_DATE -> MaterialTheme.colorScheme.primary
                        PatientStatus.PENDING -> MaterialTheme.colorScheme.tertiary
                        PatientStatus.OVERDUE -> MaterialTheme.colorScheme.error
                    },
                )
            }
        }
    }
}
